import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  TextField,
  Typography,
} from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { AddCourse } from 'src/redux/actions'
import { generateUniqueFileName, getFilePreview, ImageRatioSize } from 'src/util/helpers'
import { uploadToS3 } from 'src/util/uploadToS3'
import * as Yup from 'yup'

import { StatusList, VisibilityList } from '../../util/constants'
import DropdownMenu from '../helperComponents/DropdownMenu'
import LoadingButton from '../helperComponents/LoadingButton'

const AddEditLanguage = ({ selectedRow, toggleDrawer, refetch, products }) => {
  const dispatch = useDispatch()
  const { courseCategories, courseDifficultyLevel } = useSelector(
    store => store.mainReducer
  )

  let initialValues = {
    name: '',
    description: '',
    langIcon: '',
    video: '',
    category: 'Language',
    difficultyLevel: 'All levels',
    duration: 0,
  }

  if (selectedRow) {
    initialValues = {
      name: selectedRow.name,
      description: selectedRow.description,
      langIcon: selectedRow.langIcon,
      video: selectedRow.video,
      category: selectedRow.category,
      difficultyLevel: selectedRow.difficultyLevel,
      duration: selectedRow.duration,
      sortOrder: (selectedRow && selectedRow?.sortOrder) || '',
      id: selectedRow.id,
      isActive:
        selectedRow && selectedRow.isActive === undefined
          ? true
          : selectedRow && selectedRow?.isActive,
      isPublished:
        selectedRow && selectedRow.isPublished === undefined
          ? false
          : selectedRow && selectedRow?.isPublished,
      product: selectedRow?.product || '',
    }
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Minimum 2 characters required').max(75, 'Maximum 75 characters are allowed').required('Required'),
    // video: Yup.string().required('Please enter video url'),
    // duration: Yup.number().min(0.1),
    // description: Yup.string().min(5, 'Minimum 5 letters').required('Required'),
    sortOrder: Yup.number(),
    product: Yup.string().min(5, 'Select a product').required('Required'),
  })

  const handleCancel = () => {
    toggleDrawer(false)
  }

  const onChangeLanguageIcon = ({ target: { name, files } }, setFieldValue) => {
    if (files) {
      let file = files[0]
      file.fieldLabel = name
      setFieldValue('langIcon', file)
    }
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const reqData = selectedRow._id
        ? { ...values, _id: selectedRow._id }
        : { ...values }

      if (!reqData.id) {
        delete reqData.id
      }

      // Handle file uploads
      for (let key in reqData) {
        if (reqData[key] instanceof File) {
          let file = reqData[key]
          const uniqueName = generateUniqueFileName(file);
          const uniqueFile = new File([file], uniqueName, { type: file.type });
          await uploadToS3(uniqueFile);
          reqData[key] = uniqueName;
        }
      }
      const response = await dispatch(AddCourse(reqData));
      if (!response.error) {
        refetch();
        toggleDrawer(false);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };


  const formatProductsData = products => {
    let cloneProducts = [...products];
    return cloneProducts.map(prod => ({ key: prod.id, value: prod.name }))
  }

  return (
    <>
      <Box sx={{ m: 1 }}>
        <Container sx={{ width: "100%" }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              values,
              handleBlur,
              handleChange,
              setFieldValue,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form>
                <Grid container spacing={2}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    {/* Name Field */}
                    <TextField
                      error={Boolean(touched.name && errors.name)}
                      fullWidth
                      helperText={touched.name && errors.name}
                      label="Name"
                      margin="normal"
                      name="name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name}
                      variant="outlined"
                      required
                      size='small'
                    />

                    {/* Video URL Field (Commented) */}
                    {/* 
                      <TextField
                      error={Boolean(touched.video && errors.video)}
                     fullWidth 
                     helperText={touched.video && errors.video}
                     label="Video URLs"
                       placeholder="Video URL"
                      margin="normal"
                    name="video"
                    type={'url'}
                    onBlur={handleBlur}
                    onChange={handleChange}
                   value={values.video}
                     variant="outlined"
                    required
                      /> 
                      */}

                    {/* Language Icon Upload */}
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      sx={{
                        minHeight: 120,
                        paddingLeft: 1
                      }}
                    >
                      {/* Left Side - Add Icon Button */}
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          label=""
                          htmlFor="langIcon"
                          control={
                            <>
                              <Input
                                style={{ display: "none" }}
                                onChange={(event) => {
                                  handleChange(event);
                                  onChangeLanguageIcon(event, setFieldValue);
                                }}
                                name="langIcon"
                                inputProps={{
                                  accept: "image/*",
                                }}
                                id="langIcon"
                                type="file"
                              />
                              <Button
                                variant="contained"
                                component="span"
                                fullWidth
                                sx={{ height: 50 }}
                              >
                                Add Icon
                              </Button>
                            </>
                          }
                        />
                      </Grid>

                      {/* Right Side - Image Preview */}
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{ width: "full", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "end" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              border: "1px dashed #ccc",
                              borderRadius: 2,
                              width: "100%",
                              height: 120,
                              maxWidth: 120,
                              overflow: "hidden",
                              backgroundColor: "#fafafa",
                            }}
                          >
                            {values.langIcon ? (
                              <Box
                                sx={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                }}
                              >
                                {getFilePreview(values.langIcon)}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary", textAlign: "center" }}
                              >
                                No Icon Selected
                              </Typography>
                            )}
                          </Box>
                          <ImageRatioSize ratio="1:1" minWidth="30px" size="200kb" />
                        </Box>
                      </Grid>

                    </Grid>

                    {/* Category Dropdown (Commented) */}
                    {/* 
      <FormControl fullWidth>
        <DropdownMenu
          value={values.category}
          label="Category"
          name="category"
          onChange={handleChange}
          isRequired={true}
          data={courseCategories}
        />
      </FormControl>
      */}

                    {/* Difficulty Level Dropdown (Commented) */}
                    {/* 
      <FormControl fullWidth>
        <DropdownMenu
          value={values.difficultyLevel}
          label="Difficulty Level"
          name="difficultyLevel"
          onChange={handleChange}
          isRequired={true}
          data={courseDifficultyLevel}
        />
      </FormControl>
      */}

                    {/* Stripe Products */}
                    <FormControl
                      error={Boolean(touched.product && errors.product)}
                      fullWidth
                      margin="normal"
                    >
                      <DropdownMenu
                        value={values?.product}
                        label="Stripe Products"
                        name="product"
                        isRequired={true}
                        onChange={handleChange}
                        data={formatProductsData(products)}
                      />
                      <FormHelperText>{touched.product && errors.product}</FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    {/* Status */}
                    <FormControl fullWidth margin="normal">
                      <DropdownMenu
                        value={values.isActive}
                        label="Status"
                        name="isActive"
                        isRequired={true}
                        onChange={handleChange}
                        data={StatusList}
                      />
                    </FormControl>

                    {/* Is Published */}
                    <FormControl fullWidth margin="normal">
                      <DropdownMenu
                        value={values.isPublished}
                        label="Visibility"
                        name="isPublished"
                        isRequired={true}
                        onChange={handleChange}
                        data={VisibilityList}
                      />
                    </FormControl>

                    {/* Duration (Commented) */}
                    {/* 
      <TextField
        error={Boolean(touched.duration && errors.duration)}
        fullWidth
        helperText={touched.duration && errors.duration}
        label="Duration in hr"
        margin="normal"
        name="duration"
        type="number"
        min={1}
        onBlur={handleBlur}
        onChange={handleChange}
        value={values.duration}
        variant="outlined"
        required
      /> 
      */}

                    {/* Description (Commented) */}
                    {/* 
      <TextField
        error={Boolean(touched.description && errors.description)}
        fullWidth
        helperText={touched.description && errors.description}
        label="Description"
        margin="normal"
        name="description"
        onBlur={handleBlur}
        onChange={handleChange}
        value={values.description}
        variant="outlined"
        required
        multiline
        rows={4}
      /> 
      */}

                    {/* Sort Order */}
                    <TextField
                      fullWidth
                      label="Sort Order"
                      margin="normal"
                      inputProps={{ min: 0 }}
                      name="sortOrder"
                      type="number"
                      defaultValue={0}
                      onChange={handleChange}
                      value={values?.sortOrder}
                      variant="outlined"
                      size='small'
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} md={6}>
                    <Button
                      color="primary"
                      onClick={handleCancel}
                      fullWidth
                      size="large"
                      type="button"
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LoadingButton
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      name="Save"
                    >
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Container>
      </Box>
    </>
  )
}

export default AddEditLanguage
